/**
 * Minimal cookie read/write for this library.
 *
 * Deliberately not reusing `lib.helpers.cookies`: that package defaults every write to the
 * parent domain, and the whole point here is to control the scope of each write precisely,
 * writing `CookieConsent` host-only exactly as Cookiebot does and the mirror at the parent.
 */

export interface CookieWriteOptions {
    /** Share across subdomains. Omit to keep the cookie host-only. */
    domain?: string | null;
    /** Absolute expiry. Always anchored to the recorded decision, never to "now". */
    expires: Date;
    secure: boolean;
}

/**
 * Pulls one cookie out of a `document.cookie` string.
 *
 * Values here contain `=` (base64 stamps end `==`), so this takes everything after the
 * first separator rather than using a plain `split('=')`, which would truncate them.
 */
export const readCookieFrom = (cookieString: string, name: string): string | null => {
    const prefix = `${name}=`;
    const match = cookieString.split('; ').find((entry) => entry.indexOf(prefix) === 0);

    return match ? match.slice(prefix.length) : null;
};

/** The exact `Set-Cookie` style string a write produces. Pure, so it can be tested. */
export const serializeCookie = (
    name: string,
    value: string,
    { domain, expires, secure }: CookieWriteOptions,
): string => {
    const parts = [`${name}=${value}`, 'Path=/', `Expires=${expires.toUTCString()}`, 'SameSite=Lax'];
    if (domain) parts.push(`Domain=${domain}`);
    if (secure) parts.push('Secure');

    return parts.join('; ');
};

export const readCookie = (name: string): string | null =>
    typeof document === 'undefined' ? null : readCookieFrom(document.cookie, name);

/**
 * Writes a cookie and reports whether the browser kept it.
 *
 * A browser silently drops a write whose Domain is a public suffix or does not cover the
 * current host, so the read-back is what turns that into a signal instead of a false success.
 */
export const writeCookie = (name: string, value: string, options: Omit<CookieWriteOptions, 'secure'>): boolean => {
    if (typeof document === 'undefined') return false;

    document.cookie = serializeCookie(name, value, {
        ...options,
        secure: window.location.protocol === 'https:',
    });

    return readCookieFrom(document.cookie, name) === value;
};
